import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  toast,
} from '@kognitos/lattice';
import { UserFeedbackMessageRating } from '@/generated/hooks/zen/models';
import { useStreamingSendMessage } from '../hooks/use-quill-streaming';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { QUILL_AGENT_NAME } from '../constants';
import { NEGATIVE_OPTION_KEYS, POSITIVE_OPTION_KEYS } from './constants';
import type { IChatFeedbackProps, TFeedbackType, TPopoverView } from './types';

/**
 * FeedbackOptionsView - Shows the feedback option buttons
 */
function FeedbackOptionsView({
  type,
  onOptionSelect,
  onClose,
  disabled,
}: {
  type: TFeedbackType;
  onOptionSelect: (optionKey: string, translatedOption: string) => void;
  onClose: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('automation');
  const optionKeys =
    type === 'positive' ? POSITIVE_OPTION_KEYS : NEGATIVE_OPTION_KEYS;
  const title = t(
    type === 'positive'
      ? 'automation.quillChat.feedback.positive.title'
      : 'automation.quillChat.feedback.negative.title',
  );

  return (
    <Flex direction="column" gap={4} className="w-full">
      {/* Title row with close button */}
      <Flex align="center" justify="between" className="w-full h-6">
        <Text level="small" weight="medium" color="default">
          {title}
        </Text>
        <Button
          variant="ghost"
          size="icon-sm"
          className="size-4 opacity-70 hover:opacity-100 disabled:cursor-pointer"
          onClick={onClose}
          disabled={disabled}
          data-testid="feedback-close-btn"
        >
          <Icon type="X" className="size-3" />
        </Button>
      </Flex>

      {/* Options grid - 3 columns, 2 rows */}
      <Flex direction="column" gap={2} className="w-full">
        {/* First row */}
        <Flex gap={2} className="w-full">
          {optionKeys.slice(0, 3).map(optionKey => {
            const translatedOption = t(optionKey);
            return (
              <Button
                key={optionKey}
                variant="outline"
                size="sm"
                className="flex-1 h-9 disabled:cursor-pointer"
                onClick={() => onOptionSelect(optionKey, translatedOption)}
                disabled={disabled}
                data-testid={`feedback-option-${translatedOption.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {translatedOption}
              </Button>
            );
          })}
        </Flex>
        {/* Second row */}
        <Flex gap={2} className="w-full">
          {optionKeys.slice(3, 6).map(optionKey => {
            const translatedOption = t(optionKey);
            return (
              <Button
                key={optionKey}
                variant="outline"
                size="sm"
                className="flex-1 h-9 disabled:cursor-pointer"
                onClick={() => onOptionSelect(optionKey, translatedOption)}
                disabled={disabled}
                data-testid={`feedback-option-${translatedOption.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {translatedOption}
              </Button>
            );
          })}
        </Flex>
      </Flex>
    </Flex>
  );
}

/**
 * FeedbackOtherView - Shows the textarea for "Other" feedback
 */
function FeedbackOtherView({
  type,
  onBack,
  onSubmit,
  onClose,
  disabled,
}: {
  type: TFeedbackType;
  onBack: () => void;
  onSubmit: (text: string) => void;
  onClose: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('automation');
  const [text, setText] = useState('');
  const title = t(
    type === 'positive'
      ? 'automation.quillChat.feedback.positive.title'
      : 'automation.quillChat.feedback.negative.title',
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !disabled) {
      e.preventDefault();
      if (text.trim()) {
        onSubmit(text.trim());
      }
    }
  };

  return (
    <Flex direction="column" gap={4} className="w-full">
      {/* Title row with back button and close button */}
      <Flex align="center" justify="between" className="w-full h-6">
        <Flex align="center" gap={2}>
          <Button
            variant="ghost"
            size="icon-sm"
            className="size-6 disabled:cursor-pointer"
            onClick={onBack}
            disabled={disabled}
            data-testid="feedback-back-btn"
          >
            <Icon type="ArrowLeft" className="size-4" />
          </Button>
          <Text level="small" weight="medium" color="default">
            {title}
          </Text>
        </Flex>
        <Button
          variant="ghost"
          size="icon-sm"
          className="size-4 opacity-70 hover:opacity-100 disabled:cursor-pointer"
          onClick={onClose}
          disabled={disabled}
          data-testid="feedback-other-close-btn"
        >
          <Icon type="X" className="size-3" />
        </Button>
      </Flex>

      {/* Textarea */}
      <Textarea
        placeholder={t('automation.quillChat.feedback.otherPlaceholder')}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        className="min-h-20 resize-none disabled:cursor-text"
        autoFocus
        disabled={disabled}
        data-testid="feedback-other-textarea"
      />
    </Flex>
  );
}

/** Translation key for the "Other" option */
const OTHER_OPTION_KEY = 'automation.quillChat.feedback.other';

/**
 * FeedbackPopoverContent - Content displayed inside the feedback popover
 */
function FeedbackPopoverContent({
  type,
  onOptionSelect,
  onClose,
  disabled,
}: {
  type: TFeedbackType;
  onOptionSelect: (option: string) => void;
  onClose: () => void;
  disabled?: boolean;
}) {
  const [view, setView] = useState<TPopoverView>('options');

  const handleOptionSelect = (optionKey: string, translatedOption: string) => {
    if (optionKey === OTHER_OPTION_KEY) {
      setView('other');
    } else {
      onOptionSelect(translatedOption);
    }
  };

  const handleOtherSubmit = (text: string) => {
    onOptionSelect(text);
  };

  const handleBack = () => {
    setView('options');
  };

  if (view === 'other') {
    return (
      <FeedbackOtherView
        type={type}
        onBack={handleBack}
        onSubmit={handleOtherSubmit}
        onClose={onClose}
        disabled={disabled}
      />
    );
  }

  return (
    <FeedbackOptionsView
      type={type}
      onOptionSelect={handleOptionSelect}
      onClose={onClose}
      disabled={disabled}
    />
  );
}

/**
 * ChatFeedback - A feedback component that shows thumb up/down buttons
 * and opens a popover with feedback options when clicked.
 * Handles feedback submission internally using the streaming API.
 *
 * @param {IChatFeedbackProps} props - Component props
 * @returns {JSX.Element} Feedback button group with popovers
 *
 * @example
 * <ChatFeedback
 *   messageId="msg-123"
 *   threadId="thread-456"
 * />
 */
export function ChatFeedback({
  messageId,
  threadId,
  automationId,
}: IChatFeedbackProps) {
  const { t } = useTranslation('automation');
  const [activePopover, setActivePopover] = useState<TFeedbackType | null>(
    null,
  );
  const [submittedType, setSubmittedType] = useState<TFeedbackType | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { sendMessage } = useStreamingSendMessage({});

  const handleOptionSelect = async (type: TFeedbackType, option: string) => {
    setIsSubmitting(true);

    try {
      await sendMessage(
        currentOrganizationId,
        currentWorkspaceId,
        automationId,
        QUILL_AGENT_NAME,
        threadId,
        {
          user_message: {
            user_feedback: {
              message: messageId,
              rating:
                type === 'positive'
                  ? UserFeedbackMessageRating.RATING_POSITIVE
                  : UserFeedbackMessageRating.RATING_NEGATIVE,
              comment: option,
            },
          },
        },
      );
      // Success - mark as submitted and show success toast
      setSubmittedType(type);
      toast.success(t('automation.quillChat.feedback.success'));
    } catch {
      // Error - show error toast
      toast.error(t('automation.quillChat.feedback.error'));
    } finally {
      setIsSubmitting(false);
      setActivePopover(null);
    }
  };

  const selectedThumbClass = 'fill-[#d3d3d3] dark:fill-[#404040]';
  const keepVisible = activePopover !== null || submittedType !== null;

  return (
    <Flex
      gap={1}
      align="center"
      className={cn(
        'opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity',
        keepVisible && 'opacity-100',
      )}
    >
      {/* Thumbs Up Button with Popover */}
      <Popover
        open={activePopover === 'positive'}
        onOpenChange={open => setActivePopover(open ? 'positive' : null)}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className={cn(
                  'size-6 rounded-md',
                  activePopover === 'positive' && '!bg-accent',
                )}
                data-testid="feedback-thumbs-up-btn"
              >
                <Icon
                  type="ThumbsUp"
                  className={cn(
                    'size-4 text-muted-foreground',
                    submittedType === 'positive' && selectedThumbClass,
                  )}
                />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>

          <TooltipContent variant="primary">
            {t('automation.quillChat.feedback.positive.tooltip')}
          </TooltipContent>
        </Tooltip>

        <PopoverContent
          side="top"
          align="start"
          className="w-lg max-w-screen p-4 bg-popover border border-border shadow-md"
        >
          <FeedbackPopoverContent
            type="positive"
            onOptionSelect={option => handleOptionSelect('positive', option)}
            onClose={() => setActivePopover(null)}
            disabled={isSubmitting}
          />
        </PopoverContent>
      </Popover>

      {/* Thumbs Down Button with Popover */}
      <Popover
        open={activePopover === 'negative'}
        onOpenChange={open => setActivePopover(open ? 'negative' : null)}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className={cn(
                  'size-6 rounded-md',
                  activePopover === 'negative' && '!bg-accent',
                )}
                data-testid="feedback-thumbs-down-btn"
              >
                <Icon
                  type="ThumbsDown"
                  className={cn(
                    'size-4 text-muted-foreground',
                    submittedType === 'negative' && selectedThumbClass,
                  )}
                />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>

          <TooltipContent variant="primary">
            {t('automation.quillChat.feedback.negative.tooltip')}
          </TooltipContent>
        </Tooltip>

        <PopoverContent
          side="top"
          align="start"
          className="w-lg max-w-screen p-4 bg-popover border border-border shadow-md"
        >
          <FeedbackPopoverContent
            type="negative"
            onOptionSelect={option => handleOptionSelect('negative', option)}
            onClose={() => setActivePopover(null)}
            disabled={isSubmitting}
          />
        </PopoverContent>
      </Popover>
    </Flex>
  );
}
